import React from 'react';
import {
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Grid,
    Paper,
} from '@material-ui/core';
import DOMPurify from 'dompurify';

import { ActionButton, Block } from '@klp/design';
import { KlpPalette } from '@klp/design/Theme/KlpPalette';
import { KlpSpacing } from '@klp/design/Theme/KlpSpacing';
import { highlightSearch } from '@klp/search';

import { useKlpStyles } from '../../../design/Theme/useKlpStyles';
import type { Bygg } from '../../classes';

const byggCardStyles = () => ({
    byggTile: {
        display: 'flex',
    },
    mediaCard: {
        paddingBottom: '62%',
        marginTop: `-${KlpSpacing.bulky.mobile}`,
        marginLeft: `-${KlpSpacing.bulky.mobile}`,
        marginRight: `-${KlpSpacing.bulky.mobile}`,
    },
});

type ByggCardProps = {
    bygg: Bygg;
    search: string;
    isAdmin: boolean;
    onByggSelect: (bygg: Bygg) => void;
};

export const ByggCard: React.FC<ByggCardProps> = ({
    bygg,
    search,
    isAdmin,
    onByggSelect,
    ...props
}) => {
    const { hovedVisningsBilde, navn } = bygg;

    const classes = useKlpStyles({}, byggCardStyles);

    return (
        <Grid
            item={true}
            xs={12}
            sm={6}
            md={4}
            lg={3}
            className={classes.byggTile}
            data-testid={props['data-testid']}
        >
            <Card>
                {hovedVisningsBilde ? (
                    <CardMedia
                        className={classes.mediaCard}
                        image={hovedVisningsBilde}
                        title={navn}
                    />
                ) : (
                    <Paper
                        style={{
                            backgroundColor: KlpPalette.primary.medium,
                            borderRadius: 0,
                        }}
                        className={classes.mediaCard}
                    />
                )}
                <CardContent>
                    <Grid container={true}>
                        {bygg.navn && (
                            <Grid item={true} xs={12}>
                                <Block
                                    translation="bygg.name"
                                    klpTypographyVariant="xsmall"
                                    marginTop="compact"
                                />
                                <Block
                                    klpTypographyVariant="small"
                                    data-testid="ByggOverviewByggNavn"
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                highlightSearch(
                                                    bygg.navn,
                                                    search,
                                                ),
                                            ),
                                        }}
                                    />
                                </Block>
                            </Grid>
                        )}

                        {bygg.adresse && (
                            <Grid item={true} xs={12}>
                                <Block
                                    translation="bygg.adresse"
                                    klpTypographyVariant="xsmall"
                                    marginTop="compact"
                                />
                                <Block
                                    klpTypographyVariant="small"
                                    data-testid="ByggOverviewByggAdresse"
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                highlightSearch(
                                                    bygg.adresse,
                                                    search,
                                                ),
                                            ),
                                        }}
                                    />
                                </Block>
                            </Grid>
                        )}

                        {isAdmin && bygg.byggNummer && (
                            <Grid item={true} xs={12}>
                                <Block
                                    klpTypographyVariant="xsmall"
                                    translation="bygg.byggNummer"
                                    marginTop="compact"
                                />
                                <Block klpTypographyVariant="small">
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                highlightSearch(
                                                    bygg.byggNummer,
                                                    search,
                                                ),
                                            ),
                                        }}
                                    />
                                </Block>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
                <CardActions>
                    <ActionButton
                        actionType="forward"
                        translation="general.details"
                        klpTypographyVariant="small"
                        clickHandler={() => onByggSelect(bygg)}
                    />
                </CardActions>
            </Card>
        </Grid>
    );
};
